
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

import './CapitalRatiosChart.css';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';

interface ICapitalRatio {
    tierName: string,
    tierPercentage: number,
    tierPercentageText: string,
    featuredTier: boolean
}

interface ICapitalRatiosChartProps extends MappedComponentProperties {
    title?: string,
    tierRates?: ICapitalRatio[],
    disclaimer?: string
}

export const CapitalRatiosChartEditConfig = {
    emptyLabel: 'Capital Ratios Chart',
    isEmpty: (props: PropsWithChildren<ICapitalRatiosChartProps>) => {
        return !props || !props.disclaimer;
    },
};


const CapitalRatiosChart = (props: PropsWithChildren<ICapitalRatiosChartProps>): JSX.Element => {
    const barRef = useRef<any>();
    const [isIntersecting, setIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) =>
            setIntersecting(entry.isIntersecting),
        );
    
        observer.observe(barRef.current);
        return () => {
            observer.disconnect();
        };
    }, [barRef]);


    const getHighestPercentage = () => {
        let highestPercentage = 0;

        if (props.tierRates !== undefined) {
            for (let i = 0; i < props.tierRates.length; ++i ) {
                if (props.tierRates[i].tierPercentage > highestPercentage) {
                    highestPercentage = props.tierRates[i].tierPercentage;
                }
            }
        }

        return Math.ceil(highestPercentage / 5) * 5;

    };

    const getChart = () => {

        return <div className='CapitalRatiosChart__chart'>
            <div className='CapitalRatiosChart__chartBox'>
                <p className='CapitalRatiosChart__chartHr1Percent'>{getHighestPercentage().toString() + '%'}</p><hr className='CapitalRatiosChart__chartHr1'/>
                <p className='CapitalRatiosChart__chartHr2Percent'>{(getHighestPercentage() * (4/5)).toString() + '%'}</p><hr className='CapitalRatiosChart__chartHr2'/>
                <p className='CapitalRatiosChart__chartHr3Percent'>{(getHighestPercentage() * (3/5)).toString() + '%'}</p><hr className='CapitalRatiosChart__chartHr3'/>
                <p className='CapitalRatiosChart__chartHr4Percent'>{(getHighestPercentage() * (2/5)).toString() + '%'}</p><hr className='CapitalRatiosChart__chartHr4'/>
                <p className='CapitalRatiosChart__chartHr5Percent'>{(getHighestPercentage() * (1/5)).toString() + '%'}</p><hr className='CapitalRatiosChart__chartHr5'/>
                <p className='CapitalRatiosChart__chartHr6Percent'>{'0%'}</p><hr className='CapitalRatiosChart__chartHr6'/>
                {props.tierRates && props.tierRates.map((tierRate, index) => {
                    const width = 60 / (props.tierRates?.length ?? 1);
                    let height = '0';
                    if (isIntersecting) {    
                        height = ((tierRate.tierPercentage / getHighestPercentage()) * 100).toFixed(0);
                    }
                    return <div key={index} className='CapitalRatiosChart__chartItem' style={{ width: width + '%' }}>
                        <div className={'CapitalRatiosChart__chartPercentageText' + (tierRate.featuredTier ? '--featured': '')}>{transformHTMLParserLinks(wrapTrademarkSuperscript(tierRate.tierPercentageText))}</div>
                        <div className={'CapitalRatiosChart__chartBar' + (tierRate.featuredTier ? '--featured': '')} style={{ height: height + '%' }}></div>
                        <div className={'CapitalRatiosChart__chartBarName' + (tierRate.featuredTier ? '--featured': '')}>{transformHTMLParserLinks(wrapTrademarkSuperscript(tierRate.tierName))}</div>
                    </div>; 
                })}
            </div>
        </div>;
    };

    return (<div ref={barRef} id='capital-ratios-chart' className='CapitalRatiosChart'>
        <h3 className='CapitalRatiosChart__title'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}</h3>
        {getChart()}
        <hr className='CapitalRatiosChart__disclaimerHr'/>
        <p className='CapitalRatiosChart__disclaimer'>
                    * {transformHTMLParserLinks(props.disclaimer ?? '')}
        </p>
    </div>);
};

export default CapitalRatiosChart;
